// Canonical JSON exactly as the server writes it:
//   json.dumps(obj, sort_keys=True, separators=(",", ":"), ensure_ascii=False)
// (did_toolkit.did_webvh.jcs, rail_log.canonical, canonicaljson for the
// platform's ecdsa-jcs-2019). Only integers are allowed: a float is refused,
// as the server refuses it, so both sides always hash the same bytes.
import { utf8 } from "./encoding";

export function canonicalString(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isInteger(value)) throw new Error("JSON numbers in signed objects must be integers");
    return String(value);
  }
  if (typeof value === "string") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(canonicalString).join(",") + "]";
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort(byCodePoint);
    return "{" + keys.map((k) => JSON.stringify(k) + ":" + canonicalString(obj[k])).join(",") + "}";
  }
  throw new Error(`cannot canonicalise a ${typeof value}`);
}

// Python sorts str keys by code point; JS sort compares UTF-16 code units.
// They differ only for astral characters, so compare code points explicitly.
function byCodePoint(a: string, b: string): number {
  const A = Array.from(a), B = Array.from(b);
  for (let i = 0; i < Math.min(A.length, B.length); i++) {
    const d = A[i].codePointAt(0)! - B[i].codePointAt(0)!;
    if (d) return d;
  }
  return A.length - B.length;
}

export const canonical = (value: unknown): Uint8Array => utf8(canonicalString(value));
